import { DateTime } from "luxon";
import { appContext } from "../AppContext";
import { IDeleteBookingResponse } from "../Providers.Api/Bookings/DeleteV1BookingEndpoint";
import { IGetV2BookingResponse, IGetV2BookingResponse_BookingParty } from "../Providers.Api/Bookings/GetV2BookingEndpoint";
import * as GetV2Bookings from "../Providers.Api/Bookings/GetV2BookingsEndpoint";
import { ODataQuery } from "../Providers.Api/ODataQuery";
import { ICreateBookingResponse, ICreateV2BookingRequest, ICreateV2BookingResponse, IUpdateV2BookingRequest, IUpdateV2BookingResponse, PagedResponse } from "../Providers.Api/Models";
import Helper from "../Common/Helper";
import { IBookingDetailsRequest, ISpaceBookRequestV2, IUpdateBookingV2 } from "../Providers.IbssApiClientV2/IbssApiClientV2";
import { rights } from "../Common/RightsManager";

export class BookingService implements IBookingService
{
    private get api() { return appContext().apiClient; }
    private get local() { return appContext().localStorageProvider; }
    private get hasV2Rights() { return rights.API.Bookings.V2.has(); }

    public async get(nodeId: number, bookingId: string): Promise<IGetV2BookingResponse>
    {
        if (this.hasV2Rights)
        {
            return await this.api.bookings.getV2Booking(nodeId, bookingId);
        }
        else
        {
            const booking = await this.api.bookings.getV1Booking(nodeId, bookingId);

            const bookingParties = booking.Booking_Parties.map(x =>
            ({
                Booking_Participant_Email: x.Email,
                Booking_Participant_Name: x.First_Name + ' ' + x.Last_Name,
                Booking_Participant_Organisation: x.Company,
                Booking_Resource_Id: null,
                Booking_Visitor: false,
                Booking_Participant_Type: 1,
            } as IGetV2BookingResponse_BookingParty));

            const bookingPartiesVisitors = booking.Booking_Parties_Visitors.map(x =>
            ({
                Booking_Participant_Email: x.Email,
                Booking_Participant_Name: x.First_Name + ' ' + x.Last_Name,
                Booking_Participant_Organisation: x.Company,
                Booking_Resource_Id: null,
                Booking_Visitor: true,
                Booking_Participant_Type: 2,
            } as IGetV2BookingResponse_BookingParty));

            const v2Booking: IGetV2BookingResponse =
            {
                ...booking,
                Booking_Resources: [],
                Booking_Parties: [...bookingParties, ...bookingPartiesVisitors],
                Booking_Attendance: 0,
            };

            return v2Booking;
        }
    }

    public async getBookings(query: ODataQuery, startTime?: DateTime, endTime?: DateTime, spaceIds?: Array<string>): Promise<PagedResponse<GetV2Bookings.Booking[]>>
    {
        if (!this.hasV2Rights)
        {
            throw new Error("V1 bookings are not supported.");
        }

        const displayName = this.local.getUserDetails().displayName;
        const response = await this.api.bookings.getV2Bookings(query, startTime, endTime, spaceIds);

        response.value.forEach(i =>
        {
            if (!i.Booking_Owner_Name)
            {
                i.Booking_Owner_Name = displayName;
            }
        });

        return response;
    }

    public async create(nodeId: number, booking: ICreateV2BookingRequest): Promise<ICreateBookingResponse>
    {
        const body = {
            ...booking,
            Booking_Start: DateTime.fromISO(booking.Booking_Start as string).toUTC().toISO(),
            Booking_End: DateTime.fromISO(booking.Booking_End).toUTC().toISO(),
            Booking_Dates: booking.Booking_Dates ? booking.Booking_Dates.map(booking => ({
                ...booking,
                Booking_Start: DateTime.fromISO(booking.Booking_Start).toUTC().toISO(),
                Booking_End: DateTime.fromISO(booking.Booking_End).toUTC().toISO()
            })) : []
        } as ISpaceBookRequestV2;

        const response = await appContext().ibssApiClientV2.v2.byNodeid.bookings.post<ICreateV2BookingResponse>({
            nodeId: nodeId,
            body: body,
        });

        const bookingResponse = {
            Node_Id: response.Booking.Node_Id,
            Booking_Id: response.Booking.Booking_Id,
            Space_Id: response.Booking.Space_Id,
            Booking_Start: response.Booking.Booking_Start,
            Booking_End: response.Booking.Booking_End,
            Booking_Details: response.Booking.Booking_Details,
            Failed_Bookings_Details: response.Booking.Failed_Bookings_Details,
            Booking_Series_Id: response.Booking.Booking_Series_Id,
        };
        return bookingResponse;
    }

    public async update(nodeId: number, bookingId: string, booking: IUpdateV2BookingRequest): Promise<IUpdateV2BookingResponse>
    {
        const response = await appContext().ibssApiClientV2.v2.byNodeid.bookings.byBookingid.put<IUpdateV2BookingResponse>({
            nodeId: nodeId,
            bookingid: bookingId,
            body: booking as IUpdateBookingV2,
        });
        return response;
    }

    public delete(nodeId: number, bookingId: string, suppressErrorPopup?: boolean): Promise<IDeleteBookingResponse>
    {
        if (this.hasV2Rights)
        {
            return this.api.bookings.deleteV2Booking(nodeId, bookingId, suppressErrorPopup ?? false);
        }
        else
        {
            return this.api.bookings.deleteV1Booking(nodeId, bookingId, suppressErrorPopup ?? false);
        }
    }
}

export interface IBookingService
{
    get(nodeId: number, bookingId: string): Promise<IGetV2BookingResponse>;
    getBookings(query: ODataQuery, startTime?: DateTime, endTime?: DateTime, spaceIds?: Array<string>): Promise<PagedResponse<GetV2Bookings.Booking[]>>;
    create(nodeId: number, booking: ICreateV2BookingRequest): Promise<ICreateBookingResponse>;
    update(nodeId: number, bookingId: string, booking: IUpdateV2BookingRequest): Promise<IUpdateV2BookingResponse>;
    delete(nodeId: number, bookingId: string, suppressErrorPopup?: boolean): Promise<IDeleteBookingResponse>;
}
